import { Backdrop, CircularProgress } from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  X,
} from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API from "../../API/newIndex";
import { BardSvg } from "../../Assets/StaticData/BardSvg";
import useUnsavedChanges from "../../controllers/useUnsavedChanges";
import UnsavedChangesModal from "../../controllers/UnsavedChangesModal";
import { uploadTextEditorImage } from "../../utilities/staticFunction";
import {
  ButtonComponent,
  InputComponent,
  RichTextEditor,
  SelectComponent,
} from "@shopeasy-dashboard-ui/ui-library";
import Notification from "../Notification";

const getChangedFields = (currentValues, originalValues) => {
  if (!currentValues || !originalValues) return currentValues || {};
  const changed = {};
  Object.keys(currentValues).forEach((key) => {
    const cur = currentValues[key];
    const orig = originalValues[key];
    if (JSON.stringify(cur) !== JSON.stringify(orig)) {
      changed[key] = cur;
    }
  });
  return changed;
};

const parsePathFromRaw = (rawPath) => {
  if (!rawPath) return [];
  if (Array.isArray(rawPath)) {
    return rawPath
      .map((p) => {
        if (typeof p === "object" && p !== null) {
          return {
            link: p.link || p.value || p._id || "",
            name: p.name || p.label || p.title || p.link || p.value || "",
          };
        }
        if (typeof p === "string" && p.trim()) {
          return { link: p.trim(), name: p.trim() };
        }
        return null;
      })
      .filter(Boolean);
  }
  if (typeof rawPath === "object" && rawPath !== null) {
    return [
      {
        link: rawPath.link || rawPath.value || rawPath._id || "",
        name: rawPath.name || rawPath.label || rawPath.title || rawPath.link || rawPath.value || "",
      },
    ];
  }
  if (typeof rawPath === "string" && rawPath.trim()) {
    return [{ link: rawPath.trim(), name: rawPath.trim() }];
  }
  return [];
};

const parseFaqTypeFromRaw = (rawFaq) => {
  const typeStr = (
    rawFaq?.faq_type ||
    rawFaq?.faqtype ||
    rawFaq?.type ||
    rawFaq?.faqType ||
    "pages"
  )
    .toString()
    .toLowerCase()
    .trim();

  if (["pages", "product", "category", "blog"].includes(typeStr)) {
    return typeStr;
  }
  return typeStr || "pages";
};

const FAQ_TYPE_OPTIONS = [
  { value: "pages", label: "Pages" },
  { value: "product", label: "Product" },
  { value: "category", label: "Category" },
  { value: "blog", label: "Blog" },
];

const BulkEditFaq = ({
  isBulkEdit = true,
  bulkFaqIds = [],
  onClose,
  onSuccess,
}) => {
  const [bulkFaqsList, setBulkFaqsList] = useState([]);
  const [activeBulkIndex, setActiveBulkIndex] = useState(0);
  const [faqSearchTerm, setFaqSearchTerm] = useState("");
  const bulkFaqsDataRef = useRef({});

  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const [btnLoading, setBtnLoading] = useState(false);
  const [formSpinner, setFormSpinner] = useState(false);
  const [AiLoader, setAiloader] = useState(false);

  // Form State for Active FAQ Item
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [faqtype, setFaqtype] = useState("pages");
  const [faqSelected, setFaqSelected] = useState([]);
  const [faqpages, setFaqPages] = useState([]);
  const [faqLoading, setFaqLoading] = useState(false);
  const [warn, setWarn] = useState(false);
  const [err, setErr] = useState(false);

  const originalDataRef = useRef(null);
  const originalPayloadRef = useRef(null);

  const buildPayload = (formData = {}) => {
    return {
      question: formData.question || "",
      answer: formData.answer || "",
      faq_type: formData.faqtype || "",
      path: Array.isArray(formData.faqSelected)
        ? formData.faqSelected.map((x) => ({
          link: x.link || x.value || "",
          name: x.name || x.label || x.link || "",
        }))
        : [],
    };
  };

  const currentFormValues = useMemo(() => {
    return {
      question,
      answer,
      faqtype,
      faqSelected,
    };
  }, [question, answer, faqtype, faqSelected]);

  const isAnswerEmpty = useMemo(() => {
    if (!answer) return true;
    const text = answer
      .replace(/<style[^>]*>.*?<\/style>/gi, "")
      .replace(/<script[^>]*>.*?<\/script>/gi, "")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();
    return text.length === 0;
  }, [answer]);

  const getFaqPages = async (type) => {
    try {
      setFaqLoading(true);
      const apiCall = await API({
        url: "/admin/faq/GetFaqLinkList",
        method: "get",
        params: { type },
      });
      return apiCall?.data?.templates || [];
    } catch (error) {
      console.error("FAQ page error:", error);
      return [];
    } finally {
      setFaqLoading(false);
    }
  };

  useEffect(() => {
    const fetchFaqPages = async () => {
      const data = await getFaqPages(faqtype);
      setFaqPages(data);
    };
    if (faqtype) fetchFaqPages();
  }, [faqtype]);

  const applyFaqToForm = (rawFaq) => {
    if (!rawFaq) return;
    const rawPaths = parsePathFromRaw(rawFaq?.path);
    const parsedType = parseFaqTypeFromRaw(rawFaq);

    const initialFormValues = {
      question: rawFaq?.question || "",
      answer: rawFaq?.answer || "",
      faqtype: parsedType,
      faqSelected: rawPaths,
    };

    originalDataRef.current = initialFormValues;
    originalPayloadRef.current = buildPayload(initialFormValues);

    setQuestion(initialFormValues.question);
    setAnswer(initialFormValues.answer);
    setFaqtype(initialFormValues.faqtype);
    setFaqSelected(initialFormValues.faqSelected);
    setWarn(false);
    setErr(false);
  };

  const fetchFaqsData = async () => {
    if (!bulkFaqIds || bulkFaqIds.length === 0) return;
    setFormSpinner(true);
    try {
      const res = await API({
        url: "/admin/faq/GetFaqDataByIds",
        method: "post",
        data: { faqs: bulkFaqIds, ids: bulkFaqIds },
      });

      let loadedRecords = [];
      if (res?.status === 200 || res?.status === 304) {
        const resultData = res?.data;
        loadedRecords =
          resultData?.data?.records ||
          resultData?.records ||
          (Array.isArray(resultData?.data) ? resultData.data : []) ||
          (Array.isArray(resultData) ? resultData : []);
      }

      if (loadedRecords.length > 0) {
        setBulkFaqsList(loadedRecords);
        applyFaqToForm(loadedRecords[0]);
      } else {
        setIsNotification(true);
        setNotificationMsg("No FAQ records found for bulk edit");
        setSeverity("error");
      }
    } catch (error) {
      console.error("Error fetching bulk FAQ data:", error);
      setIsNotification(true);
      setNotificationMsg(error?.message || "Failed to fetch FAQ details");
      setSeverity("error");
    } finally {
      setFormSpinner(false);
    }
  };

  useEffect(() => {
    fetchFaqsData();
  }, []);

  const switchActiveFaq = (newIndex) => {
    if (newIndex < 0 || newIndex >= bulkFaqsList.length) return;
    if (newIndex === activeBulkIndex) return;

    const currentFaq = bulkFaqsList[activeBulkIndex];
    if (currentFaq) {
      const currentPayload = buildPayload(currentFormValues);
      const baseline = originalPayloadRef.current || originalDataRef.current;
      const changed = baseline ? getChangedFields(currentPayload, baseline) : {};
      const isModified = Object.keys(changed).length > 0;

      bulkFaqsDataRef.current[currentFaq._id] = {
        values: { ...currentFormValues },
        original: originalDataRef.current,
        originalPayload: originalPayloadRef.current,
        isModified,
      };
    }

    setActiveBulkIndex(newIndex);
    const nextFaq = bulkFaqsList[newIndex];
    if (!nextFaq) return;

    const saved = bulkFaqsDataRef.current[nextFaq._id];
    if (saved) {
      originalDataRef.current = saved.original;
      originalPayloadRef.current = saved.originalPayload;

      setQuestion(saved.values.question);
      setAnswer(saved.values.answer);
      setFaqtype(saved.values.faqtype);
      setFaqSelected(saved.values.faqSelected || []);
      setWarn(false);
      setErr(false);
    } else {
      applyFaqToForm(nextFaq);
    }
  };

  const isFaqModified = (faq, index) => {
    if (!faq) return false;
    if (index === activeBulkIndex) {
      const currentPayload = buildPayload(currentFormValues);
      const baseline = originalPayloadRef.current || originalDataRef.current;
      const changed = baseline ? getChangedFields(currentPayload, baseline) : {};
      return Object.keys(changed).length > 0;
    }
    return Boolean(bulkFaqsDataRef.current[faq._id]?.isModified);
  };

  const modifiedCount = useMemo(() => {
    return bulkFaqsList.reduce((acc, faq, idx) => {
      return acc + (isFaqModified(faq, idx) ? 1 : 0);
    }, 0);
  }, [bulkFaqsList, activeBulkIndex, currentFormValues]);

  const resetAndClose = useCallback(() => {
    bulkFaqsDataRef.current = {};
    if (onClose) onClose();
  }, [onClose]);

  const {
    showUnsavedModal,
    handleRequestClose: handleBulkEditCancel,
    handleConfirmLeave,
    handleStay,
  } = useUnsavedChanges(modifiedCount > 0, resetAndClose);

  const activeFaqQuestion = useMemo(() => {
    const active = bulkFaqsList[activeBulkIndex];
    return active?.question || "FAQ";
  }, [bulkFaqsList, activeBulkIndex]);

  const filteredFaqsList = useMemo(() => {
    if (!faqSearchTerm || !faqSearchTerm.trim()) return bulkFaqsList;
    const term = faqSearchTerm.toLowerCase();
    return bulkFaqsList.filter((faq) => {
      const q = (faq?.question || "").toLowerCase();
      const tType = (faq?.faq_type || "").toLowerCase();
      return q.includes(term) || tType.includes(term);
    });
  }, [bulkFaqsList, faqSearchTerm]);

  const removeSelectedPath = (e, link) => {
    e.preventDefault();
    setFaqSelected((prev) => prev.filter((x) => (x?.link || x?.value) !== link));
  };

  const generateAnswer = async () => {
    if (!question || !question.trim()) {
      setWarn(true);
      setIsNotification(true);
      setNotificationMsg(t("lang_Please_Enter_Question_To_Generate_Answer") || "Please enter question to generate answer");
      setSeverity("warning");
      return;
    }
    setWarn(false);
    setAiloader(true);
    try {
      const payload = { question };
      const ApiCall = await API({
        url: "/admin/ai/answer_from_questionFAQ",
        method: "post",
        data: payload,
      });
      if (ApiCall.status === 200 || ApiCall.status === 304) {
        const result = ApiCall?.data;
        setAnswer(result?.data || "");
      }
    } catch (error) {
      if (error?.response?.status === 429) {
        setIsNotification(true);
        setNotificationMsg(t("lang_Hit_chatgpt_limit") || "ChatGPT limit reached");
        setSeverity("warning");
      } else {
        setIsNotification(true);
        setNotificationMsg(error?.response?.data?.message || "Failed to generate answer");
        setSeverity("error");
      }
    } finally {
      setAiloader(false);
    }
  };

  const handleBulkUpdate = async () => {
    // Validate current active FAQ
    if (!question || !question.trim()) {
      setWarn(true);
      setErr(true);
      setIsNotification(true);
      setNotificationMsg("FAQ Question is required!");
      setSeverity("error");
      return;
    }

    try {
      setBtnLoading(true);
      setFormSpinner(true);

      const currentFaq = bulkFaqsList[activeBulkIndex];
      if (currentFaq) {
        const currentPayload = buildPayload(currentFormValues);
        const baseline = originalPayloadRef.current || originalDataRef.current;
        const changed = baseline ? getChangedFields(currentPayload, baseline) : {};
        const isModified = Object.keys(changed).length > 0;

        bulkFaqsDataRef.current[currentFaq._id] = {
          values: { ...currentFormValues },
          original: originalDataRef.current,
          originalPayload: originalPayloadRef.current,
          isModified,
        };
      }

      const modifiedEntries = Object.entries(bulkFaqsDataRef.current).filter(
        ([_, data]) => data?.isModified
      );

      if (modifiedEntries.length === 0) {
        setIsNotification(true);
        setNotificationMsg("No changes detected to update");
        setSeverity("info");
        setBtnLoading(false);
        setFormSpinner(false);
        return;
      }

      // Check validation for all modified items
      for (const [faqId, data] of modifiedEntries) {
        if (!data.values.question || !data.values.question.trim()) {
          setIsNotification(true);
          setNotificationMsg("All modified FAQs must have a Question");
          setSeverity("error");
          setBtnLoading(false);
          setFormSpinner(false);
          return;
        }
      }

      const bulkPayload = modifiedEntries
        .map(([faqId, data]) => {
          const payload = buildPayload(data.values);
          const baseline = data.originalPayload || data.original;
          const changed = getChangedFields(payload, baseline);

          const fieldsToUpdate = changed && Object.keys(changed).length > 0 ? changed : payload;

          return {
            _id: faqId,
            id: faqId,
            data: fieldsToUpdate,
            ...fieldsToUpdate,
          };
        })
        .filter(Boolean);

      if (bulkPayload.length === 0) {
        setIsNotification(true);
        setNotificationMsg("No changes detected to update");
        setSeverity("info");
        setBtnLoading(false);
        setFormSpinner(false);
        return;
      }

      const res = await API({
        url: "/admin/faq/BulkUpdateFaq",
        method: "post",
        data: bulkPayload,
      });

      if (res.status === 200 || res.status === 304) {
        setIsNotification(true);
        setNotificationMsg(
          `${bulkPayload.length} FAQ${bulkPayload.length > 1 ? "s" : ""} updated successfully!`
        );
        setSeverity("success");
        bulkFaqsDataRef.current = {};
        if (onSuccess) {
          onSuccess(bulkPayload.length);
        }
      } else {
        throw new Error(res?.data?.message || "Failed to update FAQs");
      }
    } catch (error) {
      console.error("Error bulk updating FAQs:", error);
      setIsNotification(true);
      setNotificationMsg(error?.response?.data?.message || error?.message || "Failed to update FAQs");
      setSeverity("error");
    } finally {
      setBtnLoading(false);
      setFormSpinner(false);
    }
  };

  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-[var(--primary-bg-color)] text-[var(--primary-text-color)]">
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      {(formSpinner || AiLoader) && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={formSpinner || AiLoader}
          className="flex flex-col"
        >
          <CircularProgress color="inherit" />
          <p className="mt-2 text-sm">
            {AiLoader ? t("lang_Please_Wait") || "Generating content..." : t("lang_loading") || "Processing..."}
          </p>
        </Backdrop>
      )}

      {/* Top Header Bar */}
      <div className="flex items-center justify-between px-6 py-3.5 border-b border-[var(--border-color)] bg-[var(--secondary-bg-color)] shrink-0">
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={handleBulkEditCancel}
            className="w-7 h-7 rounded-full flex items-center justify-center text-[var(--table-text-color)] hover:bg-[var(--focus-tab-color)] hover:text-[var(--primary-text-color)] transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h5 className="text-sm font-bold text-[var(--primary-text-color)] m-0">
                Bulk Edit FAQs
              </h5>
              <span className="bg-[var(--focus-tab-color)] text-[var(--primary-text-color)] text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[var(--border-color)]">
                {bulkFaqsList.length} Selected
              </span>
            </div>
            <p className="text-[11px] text-[var(--table-text-color)] m-0 mt-0.5">
              Select any FAQ from the list to view and edit its details.
            </p>
          </div>
        </div>


      </div>

      {/* Main Body */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Sidebar Panel */}
        <div className="w-[300px] border-r border-[var(--border-color)] bg-[var(--primary-bg-color)] p-4 flex flex-col shrink-0">
          {/* Outer Rounded Container */}
          <div className="flex-1 flex flex-col border border-[var(--border-color)] rounded-2xl bg-[var(--secondary-bg-color)] overflow-hidden shadow-[var(--shadow-card)]">
            {/* Header section */}
            <div className="p-3.5 border-b border-[var(--border-color)] flex items-center justify-between">
              <span className="text-xs font-bold text-[var(--primary-text-color)]">
                Selected FAQs
              </span>
              <div className="flex items-center space-x-1 text-xs text-[var(--table-text-color)]">
                <button
                  type="button"
                  disabled={activeBulkIndex === 0}
                  onClick={() => switchActiveFaq(activeBulkIndex - 1)}
                  className="p-1 rounded hover:bg-[var(--focus-tab-color)] disabled:opacity-30 disabled:pointer-events-none cursor-pointer text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="font-mono text-[11px] text-[var(--table-text-color)]">
                  {activeBulkIndex + 1}/{bulkFaqsList.length}
                </span>
                <button
                  type="button"
                  disabled={activeBulkIndex === bulkFaqsList.length - 1}
                  onClick={() => switchActiveFaq(activeBulkIndex + 1)}
                  className="p-1 rounded hover:bg-[var(--focus-tab-color)] disabled:opacity-30 disabled:pointer-events-none cursor-pointer text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>

            {/* Filter Search Input Section */}
            <div className="p-3 border-b border-[var(--border-color)] bg-transparent">
              <input
                type="text"
                placeholder="Filter selected FAQs..."
                value={faqSearchTerm}
                onChange={(e) => setFaqSearchTerm(e.target.value)}
                className="w-full px-3 py-1.5 text-xs border border-[var(--border-color)] rounded-lg bg-[var(--primary-bg-color)] text-[var(--primary-text-color)] placeholder-[var(--table-text-color)] focus:outline-none focus:border-[var(--focus-border-color)] shadow-xs"
              />
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {filteredFaqsList.map((faq) => {
                const actualIndex = bulkFaqsList.findIndex((item) => item._id === faq._id);
                const isActive = actualIndex === activeBulkIndex;
                const isModified = isFaqModified(faq, actualIndex);
                const displayQuestion =
                  isActive ? (question || faq.question || "-") : (faq.question || "-");
                const currentType = isActive ? (faqtype || parseFaqTypeFromRaw(faq)) : parseFaqTypeFromRaw(faq);
                const displayTypeLabel =
                  FAQ_TYPE_OPTIONS.find((opt) => opt.value === currentType)?.label || currentType || "Pages";

                return (
                  <div
                    key={faq._id}
                    onClick={() => switchActiveFaq(actualIndex)}
                    className={`p-3 cursor-pointer transition-colors flex items-center justify-between gap-3 border-b border-[var(--border-color)] last:border-b-0 ${isActive
                        ? "bg-[var(--focus-tab-color)]"
                        : "bg-[var(--secondary-bg-color)] hover:bg-[var(--focus-tab-color)]"
                      }`}
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-md bg-[var(--primary-bg-color)] border border-[var(--border-color)] flex items-center justify-center shrink-0 overflow-hidden p-1 text-[var(--table-text-color)]">
                        <HelpCircle size={18} />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span
                          className={`text-xs truncate ${isActive
                              ? "font-bold text-[var(--primary-text-color)]"
                              : "font-medium text-[var(--primary-text-color)]"
                            }`}
                        >
                          {displayQuestion}
                        </span>
                        <span className="text-[11px] text-[var(--table-text-color)] truncate mt-0.5 capitalize">
                          {displayTypeLabel}
                        </span>
                      </div>
                    </div>

                    {isModified && (
                      <span
                        title='Modified'
                        className={`w-2 h-2 rounded-full bg-amber-500 shrink-0 select-none inline-block`}
                      />
                    )}
                  </div>
                );
              })}

              {filteredFaqsList.length === 0 && (
                <div className="p-4 text-center text-xs text-[var(--table-text-color)]">
                  No FAQs matching &quot;{faqSearchTerm}&quot;
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Main Form Content Area */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-[var(--primary-bg-color)]">
          {/* Right Sub-Header Bar */}
          <div className="flex items-center justify-between px-6 py-2.5 bg-[var(--secondary-bg-color)] border-b border-[var(--border-color)] text-xs shrink-0">
            <div>
              <span className="font-bold text-[var(--primary-text-color)]">
                Currently Editing: {activeFaqQuestion}
              </span>{" "}
              <span className="text-[var(--table-text-color)] font-mono text-[11px]">
                ({activeBulkIndex + 1} of {bulkFaqsList.length})
              </span>
            </div>
            <div>
              {modifiedCount > 0 ? (
                <span className="text-emerald-500 font-medium text-xs">
                  {modifiedCount} FAQ modified
                </span>
              ) : (
                <span className="text-[var(--table-text-color)] text-xs font-normal">
                  No unsaved changes
                </span>
              )}
            </div>
          </div>

          {/* Form Area matching UpdateFaq / AddFaq exactly */}
          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            <form
              onKeyDown={(e) => e.key === "Enter" && e.target.tagName !== "TEXTAREA" && e.preventDefault()}
              className="max-w-4xl mx-auto space-y-5"
            >
              <div className="bg-[var(--secondary-bg-color)] border border-[var(--border-color)] rounded-2xl p-6 shadow-[var(--shadow-card)] space-y-4">
                {/* Question */}
                <div>
                  <InputComponent
                    id="question"
                    name="question"
                    label={t("lang_Question") || "Question"}
                    required={true}
                    value={question}
                    onChange={(e) => {
                      setQuestion(e.target.value);
                      setWarn(false);
                      setErr(false);
                    }}
                    placeholder="Write your question"
                    error={err && !question.trim() ? t("lang_Please_Enter_Question") || "Please enter question" : ""}
                    helpText={warn ? t("lang_Please_Enter_Question_To_Generate_Answer") || "Please enter question to generate answer" : ""}
                  />
                </div>

                {/* Answer */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label htmlFor="Answer">
                      <p className="text-[13px] font-medium text-[var(--primary-text-color)] m-0">
                        {t("lang_Answer") || "Answer"}
                        <span className="text-red-500 ms-1">*</span>
                      </p>
                    </label>
                    <div className="flex items-center">
                      <span
                        className="inline-flex items-center cursor-pointer hover:underline text-[13px] rounded-[6px] transition duration-300 px-1 text-[var(--primary-text-color)]"
                        onClick={generateAnswer}
                      >
                        <BardSvg /> {t("lang_Get_Answer") || "Get Answer"}
                      </span>
                    </div>
                  </div>
                  <RichTextEditor
                    htmlData={answer}
                    setHtmlData={(val) => setAnswer(val)}
                    uplaodImageFile={uploadTextEditorImage}
                  />
                  {err && isAnswerEmpty && (
                    <p className="text-red-500 font-medium text-xs mt-1">
                      {t("lang_Please_Enter_Answer") || "Please enter answer"}
                    </p>
                  )}
                </div>

                {/* FAQ Type */}
                <div className="w-full">
                  <SelectComponent
                    id="faqtype"
                    name="faqtype"
                    label={t("lang_FAQ_Type") || "FAQ Type"}
                    required={true}
                    value={faqtype}
                    onChange={(e) => {
                      setFaqtype(e.target.value);
                      setFaqSelected([]);
                    }}
                    options={[
                      { value: "pages", label: t("lang_pages") || "Pages" },
                      { value: "product", label: t("lang_Product") || "Product" },
                      { value: "category", label: t("lang_category") || "Category" },
                      { value: "blog", label: t("lang_blog") || "Blog" },
                    ]}
                  />
                </div>

                {/* Path */}
                {faqpages?.length > 0 && (
                  <div>
                    <SelectComponent
                      id="faqPath"
                      name="faqPath"
                      label={t("lang_path") || "Path"}
                      required={true}
                      placeholder="Select Path"
                      loading={faqLoading}
                      value=""
                      onChange={(e) => {
                        const selectedLink = e.target.value;
                        const foundItem = faqpages.find(
                          (x) => x.link === selectedLink || x.value === selectedLink,
                        );
                        if (foundItem) {
                          setFaqSelected((prev) => {
                            const alreadyExist = prev.some((item) => item.link === foundItem.link);
                            if (alreadyExist) return prev;
                            return [...prev, foundItem];
                          });
                        }
                      }}
                      options={faqpages?.map((item) => ({
                        value: item.link,
                        label: item.name ? `${item.name} (${item.link})` : item.link,
                      }))}
                    />
                  </div>
                )}

                {/* Selected Path Tags */}
                <div className="flex flex-wrap mt-2">
                  {faqSelected?.map((elemItem, index) => {
                    return (
                      <div
                        key={index}
                        className="bg-[var(--primary-bg-color)] flex items-center me-2 mb-2 min-w-[50px] text-xs text-left px-3 capitalize py-1.5 rounded-[6px] border border-[var(--border-color)]"
                      >
                        <span className="text-[var(--primary-text-color)]">{elemItem?.name || elemItem?.link}</span>
                        <X
                          className="h-[14px] text-red-500 hover:text-red-700 min-w-[14px] max-w-[14px] ms-1.5 cursor-pointer"
                          onClick={(e) => removeSelectedPath(e, elemItem?.link)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Action Bar */}
      <div className="flex items-center justify-between w-full border-t border-[var(--border-color)] bg-[var(--secondary-bg-color)] p-3.5 px-6 shrink-0">
        <button
          type="button"
          onClick={handleBulkEditCancel}
          className="flex items-center text-xs font-medium text-[var(--table-text-color)] hover:text-[var(--primary-text-color)] transition-colors gap-1 cursor-pointer"
        >
          ← cancel
        </button>

        <div className="flex items-center gap-2">
          <ButtonComponent
            variant="primary"
            size="sm"
            loading={btnLoading}
            disabled={modifiedCount === 0 || btnLoading}
            onClick={handleBulkUpdate}
          >
            {modifiedCount > 0 ? `Save Changes (${modifiedCount})` : "Save Changes"}
          </ButtonComponent>
        </div>
      </div>

      <UnsavedChangesModal
        open={showUnsavedModal}
        onStay={handleStay}
        onLeave={handleConfirmLeave}
      />
    </div>
  );
};

export default BulkEditFaq;
